import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { Toast } from '../../components/base/Toast';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/format';
import { useBranding } from '../../context/BrandingContext';

interface UserInfo { id: string; nom: string; role: string; }

export default function RapportPersonnelDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { branding } = useBranding();
  
  const [user, setUser] = useState<UserInfo | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [personnel, setPersonnel] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Initialize dates from query string or default to current month
  const [dateDebut, setDateDebut] = useState(() => {
    return searchParams.get('debut') || (() => {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      return firstDay.toISOString().split('T')[0];
    })();
  });

  const [dateFin, setDateFin] = useState(() => {
    return searchParams.get('fin') || (() => {
      return new Date().toISOString().split('T')[0];
    })();
  });

  useEffect(() => {
    const token = localStorage.getItem('atelier_bassoum_token');
    const stored = localStorage.getItem('atelier_bassoum_user');
    if (stored && token) {
      setUser(JSON.parse(stored));
      loadData(token);
    } else {
      navigate('/login');
    }
  }, [navigate, id]);

  const loadData = async (token: string, debutStr = dateDebut, finStr = dateFin) => {
    if (!id) return;
    setLoading(true);
    try {
      const [pData, hData] = await Promise.all([
        api.getPersonnelById(token, id),
        api.getPersonnelHistory(token, id, debutStr, finStr)
      ]);
      setPersonnel(pData);
      setHistory(hData);
    } catch (err) {
      setToast({ message: 'Erreur lors du chargement des données', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (token) {
      // Sync date params to URL
      setSearchParams({ debut: dateDebut, fin: dateFin });
      loadData(token, dateDebut, dateFin);
    }
  };

  // KPI Calculations
  const totalTasksAssigned = history.length;
  const completedTasks = history.filter(h => h.statut === 'TERMINE');
  const totalTasksCompleted = completedTasks.length;
  
  const completionRate = totalTasksAssigned > 0 
    ? Math.round((totalTasksCompleted / totalTasksAssigned) * 100) 
    : 0;

  const totalGainsValidated = completedTasks.reduce(
    (sum, t) => sum + (parseFloat(t.cout_main_doeuvre) || 0), 0
  );

  const totalGainsPotential = history.reduce(
    (sum, t) => sum + (parseFloat(t.cout_main_doeuvre) || 0), 0
  );

  const avgGainsPerTask = totalTasksCompleted > 0
    ? Math.round(totalGainsValidated / totalTasksCompleted)
    : 0;

  // Filter history by search query
  const filteredHistory = history.filter(item => {
    const q = searchQuery.toLowerCase();
    return (
      !q ||
      (item.commande_code || '').toLowerCase().includes(q) ||
      (item.client_nom || '').toLowerCase().includes(q) ||
      (item.tache || '').toLowerCase().includes(q) ||
      (item.article_nom || '').toLowerCase().includes(q)
    );
  });

  const formatDateFr = (dateStr: string) => {
    if (!dateStr) return '-';
    const datePart = dateStr.includes(' ') ? dateStr.split(' ')[0] : dateStr;
    const parts = datePart.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (filteredHistory.length === 0) {
      setToast({ message: 'Aucune donnée à exporter', type: 'error' });
      return;
    }

    const exportRows = filteredHistory.map(item => ({
      'Référence Commande': item.commande_code || `CMD-${item.commande_id}`,
      'Client': item.client_nom || 'N/A',
      'Article / Modèle': item.article_nom || item.modele_nom || 'Sur mesure',
      'Tâche': item.tache,
      'Quantité': item.quantite || 1,
      'Coût Main d\'œuvre (FCFA)': parseFloat(item.cout_main_doeuvre) || 0,
      'Statut': item.statut,
      'Date assignation': formatDateFr(item.date_assignation || item.created_at),
      'Date validation': item.completed_at ? formatDateFr(item.completed_at) : '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Historique Tâches');
    
    // Auto-fit column widths
    const maxLens = Object.keys(exportRows[0]).map(key => {
      const lens = exportRows.map(r => String((r as any)[key] || '').length);
      return Math.max(key.length, ...lens) + 2;
    });
    worksheet['!cols'] = maxLens.map(w => ({ wch: w }));

    const fileName = `rapport_taches_${personnel?.nom?.toLowerCase()?.replace(/\s+/g, '_')}_${dateDebut}_${dateFin}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    setToast({ message: 'Export Excel réussi !', type: 'success' });
  };

  // Export to PDF
  const handleExportPDF = () => {
    if (filteredHistory.length === 0) {
      setToast({ message: 'Aucune donnée à exporter', type: 'error' });
      return;
    }

    const doc = new jsPDF();
    
    // Title & Info
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`Rapport d'activité individuel`, 14, 20);
    
    doc.setFontSize(11);
    doc.setFont('Helvetica', 'normal');
    doc.text(`Nom: ${personnel?.nom}`, 14, 28);
    doc.text(`Spécialité: ${personnel?.specialite || personnel?.role || 'Personnel'}`, 14, 34);
    doc.text(`Période: ${formatDateFr(dateDebut)} au ${formatDateFr(dateFin)}`, 14, 40);
    
    // KPI summary table
    autoTable(doc, {
      startY: 46,
      head: [['Tâches Assignées', 'Tâches Terminées', 'Taux de Complétion', 'Gains Validés (FCFA)']],
      body: [[
        totalTasksAssigned,
        totalTasksCompleted,
        `${completionRate}%`,
        formatPrice(totalGainsValidated, false)
      ]],
      theme: 'grid',
      styles: { halign: 'center', cellPadding: 3, fontSize: 9 },
      headStyles: { fillColor: [40, 92, 73], textColor: [255, 255, 255] }
    });

    // History Table
    const headers = [['Réf.', 'Client', 'Modèle', 'Tâche', 'Qte', 'Gain (FCFA)', 'Statut', 'Fait le']];
    const rows = filteredHistory.map(item => [
      item.commande_code || `#${item.commande_id}`,
      item.client_nom || '-',
      item.article_nom || 'Sur mesure',
      item.tache,
      item.quantite || 1,
      formatPrice(parseFloat(item.cout_main_doeuvre) || 0, false),
      item.statut,
      item.completed_at ? formatDateFr(item.completed_at) : '-'
    ]);

    autoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY + 10,
      head: headers,
      body: rows,
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [79, 70, 229] },
      alternateRowStyles: { fillColor: [249, 250, 251] }
    });

    const fileName = `rapport_taches_${personnel?.nom?.toLowerCase()?.replace(/\s+/g, '_')}_${dateDebut}_${dateFin}.pdf`;
    doc.save(fileName);
    setToast({ message: 'Export PDF réussi !', type: 'success' });
  };

  if (!user) return null;

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      <div className="hidden md:block">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center">
            <button 
              onClick={() => navigate(`/rapports?tab=personnel&debut=${dateDebut}&fin=${dateFin}`)} 
              className="p-2 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-all border border-gray-200/80 mr-4 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h2 className="font-playfair font-bold text-xl text-gray-900">
                {loading ? 'Chargement...' : `Rapport de ${personnel?.nom}`}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {personnel?.specialite || personnel?.role || 'Personnel'} · Historique & Activité
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportExcel}
              className="py-2.5 px-4 bg-green-50 text-green-700 hover:bg-green-600 hover:text-white border border-green-100 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-sm shadow-green-50"
            >
              <i className="ri-file-excel-2-line text-sm" />
              <span className="hidden sm:inline">Excel</span>
            </button>
            <button
              onClick={handleExportPDF}
              className="py-2.5 px-4 bg-red-50 text-red-700 hover:bg-red-600 hover:text-white border border-red-100 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer shadow-sm shadow-red-50"
            >
              <i className="ri-file-pdf-line text-sm" />
              <span className="hidden sm:inline">PDF</span>
            </button>
          </div>
        </header>

        {/* Date Filter Panel */}
        <section className="bg-white border-b border-gray-100 px-6 py-3 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 focus-within:ring-2 focus-within:ring-foret-500">
                <span className="text-[10px] font-black text-gray-400 uppercase">Du</span>
                <input 
                  type="date" 
                  value={dateDebut} 
                  onChange={e => setDateDebut(e.target.value)} 
                  className="bg-transparent border-none text-xs font-bold text-gray-800 focus:outline-none cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 focus-within:ring-2 focus-within:ring-foret-500">
                <span className="text-[10px] font-black text-gray-400 uppercase">Au</span>
                <input 
                  type="date" 
                  value={dateFin} 
                  onChange={e => setDateFin(e.target.value)} 
                  className="bg-transparent border-none text-xs font-bold text-gray-800 focus:outline-none cursor-pointer"
                />
              </div>
              <button 
                onClick={handleRefresh}
                className="p-2 bg-foret-500 hover:bg-foret-600 text-white rounded-xl transition-all cursor-pointer flex items-center justify-center border border-foret-600"
                title="Filtrer"
              >
                <i className="ri-filter-2-line text-sm" />
              </button>
            </div>
            
            <div className="w-full sm:w-64">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 transition-all focus-within:ring-2 focus-within:ring-foret-500">
                <i className="ri-search-line text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Rechercher une tâche, client..." 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="bg-transparent border-none text-xs text-gray-800 focus:outline-none w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Scrollable Dashboard Body */}
        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-32">
              <div className="w-10 h-10 border-4 border-foret-200 border-t-foret-600 rounded-full animate-spin mb-4" />
              <p className="text-gray-400 text-sm">Chargement des données...</p>
            </div>
          ) : (
            <>
              {/* KPIs Row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    label: "Gains Validés",
                    value: formatPrice(totalGainsValidated),
                    sub: `Sur un potentiel de ${formatPrice(totalGainsPotential)}`,
                    icon: "ri-wallet-3-line",
                    color: "text-green-600 bg-green-50 border-green-100"
                  },
                  {
                    label: "Taux de Complétion",
                    value: `${completionRate}%`,
                    sub: `${totalTasksCompleted} finies sur ${totalTasksAssigned} assignées`,
                    icon: "ri-percent-line",
                    color: "text-foret-600 bg-foret-50 border-foret-100"
                  },
                  {
                    label: "Tâches Terminées",
                    value: totalTasksCompleted,
                    sub: "Tâches validées par l'atelier",
                    icon: "ri-checkbox-circle-line",
                    color: "text-blue-600 bg-blue-50 border-blue-100"
                  },
                  {
                    label: "Moyenne par Tâche",
                    value: formatPrice(avgGainsPerTask),
                    sub: "Gains moyens par tâche finie",
                    icon: "ri-calculator-line",
                    color: "text-purple-600 bg-purple-50 border-purple-100"
                  }
                ].map((kpi, idx) => (
                  <div key={idx} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest">{kpi.label}</span>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base border ${kpi.color}`}>
                        <i className={kpi.icon} />
                      </div>
                    </div>
                    <div>
                      <p className="text-xl font-extrabold text-gray-900">{kpi.value}</p>
                      <p className="text-[10px] text-gray-400 mt-1 font-bold">{kpi.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Task History Panel */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
                  <h3 className="font-playfair font-bold text-sm text-gray-800">Historique des tâches ({filteredHistory.length})</h3>
                  <span className="text-[10px] font-black uppercase bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full">
                    {formatDateFr(dateDebut)} au {formatDateFr(dateFin)}
                  </span>
                </div>

                {filteredHistory.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-300 mb-4 border border-gray-100">
                      <i className="ri-folder-open-line text-2xl" />
                    </div>
                    <p className="text-sm font-bold text-gray-500">Aucune tâche trouvée</p>
                    <p className="text-xs text-gray-400 mt-1">Aucune activité enregistrée ou correspondant aux filtres pour cette période.</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile View: Tasks Cards */}
                    <div className="md:hidden divide-y divide-gray-50 pb-20">
                      {filteredHistory.map(item => (
                        <div key={item.id} className="p-4 space-y-3 bg-white">
                          <div className="flex items-center justify-between">
                            <span 
                              onClick={() => navigate(`/commande/${item.commande_id}`)}
                              className="text-[10px] font-black text-gray-400 hover:text-foret-600 transition-colors cursor-pointer border-b border-dashed border-gray-300 animate-pulse"
                            >
                              {item.commande_code || `CMD-#${item.commande_id}`}
                            </span>
                            <span className={`text-[9px] font-black px-2 py-0.5 rounded-full border ${
                              item.statut === 'TERMINE' 
                                ? 'bg-green-50 text-green-700 border-green-150' 
                                : item.statut === 'EN_COURS' 
                                  ? 'bg-purple-50 text-purple-700 border-purple-150' 
                                  : 'bg-amber-50 text-amber-700 border-amber-150'
                            }`}>
                              {item.statut}
                            </span>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{item.client_nom || 'Client inconnu'}</p>
                            <h4 className="text-sm font-black text-gray-900 mt-0.5">{item.tache}</h4>
                            <p className="text-xs text-gray-500 font-bold mt-1 uppercase tracking-tight">{item.article_nom || 'Sur mesure'}</p>
                          </div>
                          <div className="flex items-center justify-between pt-2 border-t border-gray-50 text-[11px] font-bold">
                            <span className="text-gray-400">Gain : {formatPrice(item.cout_main_doeuvre)}</span>
                            <span className="text-gray-400">
                              {item.completed_at ? `Validé le : ${formatDateFr(item.completed_at)}` : `Assigné le : ${formatDateFr(item.date_assignation || item.created_at)}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop View: Table */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            {['Commande', 'Client', 'Article/Modèle', 'Tâche', 'Qte', 'Gain', 'Statut', 'Date Activité'].map(h => (
                              <th key={h} className="text-left text-[10px] font-bold text-gray-400 uppercase px-6 py-3 whitespace-nowrap tracking-wider">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 text-sm">
                          {filteredHistory.map(item => (
                            <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                              <td className="px-6 py-3.5">
                                <button
                                  onClick={() => navigate(`/commande/${item.commande_id}`)}
                                  className="text-xs font-black text-gray-500 hover:text-foret-600 transition-colors border-b border-dashed border-gray-300 hover:border-foret-500 cursor-pointer"
                                >
                                  {item.commande_code || `CMD-${item.commande_id}`}
                                </button>
                              </td>
                              <td className="px-6 py-3.5 font-bold text-gray-800">
                                {item.client_nom || 'Client inconnu'}
                              </td>
                              <td className="px-6 py-3.5">
                                <span className="text-xs font-bold text-gray-400 uppercase tracking-tight">
                                  {item.article_nom || 'Sur mesure'}
                                </span>
                              </td>
                              <td className="px-6 py-3.5 font-bold text-gray-750">
                                {item.tache}
                              </td>
                              <td className="px-6 py-3.5 font-bold text-gray-500">
                                {item.quantite || 1}
                              </td>
                              <td className="px-6 py-3.5 font-bold text-foret-700">
                                {formatPrice(item.cout_main_doeuvre)}
                              </td>
                              <td className="px-6 py-3.5">
                                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                                  item.statut === 'TERMINE' 
                                    ? 'bg-green-50 text-green-700 border-green-150' 
                                    : item.statut === 'EN_COURS' 
                                      ? 'bg-purple-50 text-purple-700 border-purple-150' 
                                      : 'bg-amber-50 text-amber-700 border-amber-150'
                                }`}>
                                  {item.statut}
                                </span>
                              </td>
                              <td className="px-6 py-3.5 text-xs text-gray-400 font-bold">
                                {item.completed_at 
                                  ? `Fait le: ${formatDateFr(item.completed_at)}` 
                                  : `Assigné: ${formatDateFr(item.date_assignation || item.created_at)}`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
